/**
 * NIIMBOT B4 Bluetooth Printer Driver
 * Protocol: https://github.com/MultiMote/niimbluelib
 *
 * ROOT CAUSE FIX (dari analisis ACK log):
 * Printer B4 V3 mengirim async DeviceInfo notification (0x1B) saat print dimulai.
 * prepareAck() lama menangkap notification ini sebagai "ACK untuk setPageSize 0x1A"
 * padahal itu adalah informasi device yang tidak diminta — false positive.
 *
 * Fix: prepareAck() sekarang menerima `expectedCmd` dan hanya resolve
 * jika response command = expectedCmd. Semua ACK lain diabaikan.
 *
 * Command → Expected Response mapping (dari niimbluelib commandsMap):
 *   0xC1 Connect        → 0xC2
 *   0xDC Advanced2      → 0xD9
 *   0xA3 GetStatus      → 0xB3 (V3) atau 0xA4 (V2)
 *   0x21 SetDensity     → 0x31
 *   0x23 SetLabelType   → 0x33
 *   0x01 PrintStart     → 0x02
 *   0x03 PageStart      → 0x04
 *   0x13 SetPageSize V2 → 0x14
 *   0x1A SetPageSize V3 → 0x1B  ← tapi 0x1B juga dipakai untuk DeviceInfo!
 *   0xE3 PageEnd        → 0xE4
 *   0xF3 PrintEnd       → 0xF4
 */
class NiimbotB4 {
  constructor() {
    this.serviceUuid = 'e7810a71-73ae-499d-8c15-faa9aef0c3f2';
    this.charUuid = 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f';
    this.device = null;
    this.server = null;
    this.characteristic = null;
    this.isPrinting = false;
    this.MAX_WIDTH = 800;
    this.protocolVersion = 0;
    this.isV3 = false;
  }

  // ─── ACK Wait ─────────────────────────────────────────────────────────────

  /**
   * Tunggu response dengan command byte tertentu.
   *
   * @param {number} expectedCmd - Byte command yang diharapkan di posisi raw[2]
   * @param {number} timeout     - Milliseconds sebelum timeout (default 2500)
   *
   * Kenapa perlu expectedCmd:
   * Printer B4 V3 mengirim async notification (misal 0x1B = DeviceInfo, 0xD3 = progress)
   * kapan saja. Tanpa filter, listener menangkap notification yang salah dan mengira
   * itu adalah ACK untuk command yang baru dikirim.
   *
   * Contoh masalah dari log:
   *   Kita kirim 0x1A (setPageSize V3), expect ACK 0x1B
   *   Printer kebetulan kirim DeviceInfo notification (juga 0x1B, 42 bytes)
   *   → prepareAck lama resolve dengan DeviceInfo, bukan ACK setPageSize
   *   → Kode mengira setPageSize gagal, padahal belum tentu
   *
   * Solusi: filter tambahan — untuk 0x1B, ACK yang sah hanya 1 data byte (length = 0x01)
   * DeviceInfo 0x1B memiliki length = 0x2A (42 bytes) — sangat berbeda.
   */
  prepareAck(expectedCmd, timeout = 2500) {
    return new Promise((resolve) => {
      if (!this.characteristic) return resolve(null);

      const timer = setTimeout(() => {
        this.characteristic.removeEventListener('characteristicvaluechanged', handler);
        resolve(null);
      }, timeout);

      const handler = (e) => {
        const raw = new Uint8Array(e.target.value.buffer);
        if (raw.length < 5) return; // paket terlalu pendek, abaikan

        const responseCmd = raw[2];
        const dataLen     = raw[3];

        // Abaikan progress / heartbeat
        if (responseCmd === 0xD3 || responseCmd === 0xC6) return;

        // On some B4 models, the response to 0x1A (SetPageSize) IS the 0x1B DeviceInfo packet.
        // We should accept 0x1B regardless of length if that's what we're waiting for.
        if (responseCmd !== expectedCmd) {
          if (responseCmd === 0x1B) {
             console.log(`[NIIMBOT] Skipping async DeviceInfo (0x1B len=${dataLen})`);
          } else {
             console.log(`[NIIMBOT] Skipping unexpected cmd=0x${responseCmd.toString(16)} (waiting for 0x${expectedCmd.toString(16)})`);
          }
          return;
        }

        clearTimeout(timer);
        this.characteristic.removeEventListener('characteristicvaluechanged', handler);
        console.log(`[NIIMBOT] ACK 0x${responseCmd.toString(16).padStart(2,'0')}:`, 
          Array.from(raw).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
        resolve(raw);
      };

      this.characteristic.addEventListener('characteristicvaluechanged', handler);
    });
  }

  // ─── Connection ───────────────────────────────────────────────────────────

  async autoConnect() {
    if (!navigator.bluetooth || !navigator.bluetooth.getDevices) return false;
    try {
      const devices = await navigator.bluetooth.getDevices();
      const found = devices.find(d => d.name && (
        d.name.includes('NIIMBOT') || d.name.includes('B4') ||
        d.name.includes('B1')      || d.name.includes('B21')
      ));
      if (found) {
        console.log('[NIIMBOT] Found cached device:', found.name);
        this.device = found;
        return true;
      }
    } catch (err) {
      console.warn('[NIIMBOT] Silent discovery failed:', err.message);
    }
    return false;
  }

  async connect() {
    try {
      if (!this.device) {
        console.log('[NIIMBOT] Opening Bluetooth picker...');
        this.device = await navigator.bluetooth.requestDevice({
          filters: [
            { namePrefix: 'NIIMBOT' },
            { namePrefix: 'B4' },
            { namePrefix: 'B1' },
            { namePrefix: 'B21' },
          ],
          optionalServices: [this.serviceUuid]
        });
      } else {
        console.log('[NIIMBOT] Reusing cached device:', this.device.name);
      }

      this.server = await this.device.gatt.connect();
      const service = await this.server.getPrimaryService(this.serviceUuid);
      this.characteristic = await service.getCharacteristic(this.charUuid);
      await this.characteristic.startNotifications();

      console.log('[NIIMBOT] Connected to', this.device.name);
      await this.handshake();
      return true;
    } catch (error) {
      this.device = null;
      if (error.name === 'NotFoundError') throw new Error('Printer selection cancelled.');
      throw error;
    }
  }

  // ─── Packet Builder ───────────────────────────────────────────────────────

  buildPacket(cmd, data = []) {
    const payload = [cmd, data.length, ...data];
    let checksum = 0;
    payload.forEach(b => (checksum ^= b));
    payload.push(checksum);
    return new Uint8Array([0x55, 0x55, ...payload, 0xAA, 0xAA]);
  }

  /**
   * Kirim command dan tunggu ACK dengan response command yang spesifik.
   * @param {number} cmd         - Command byte
   * @param {number} responseCmd - Expected response command byte (cmd + 1 biasanya)
   * @param {Array}  data        - Payload bytes
   * @param {boolean} critical   - Throw jika timeout
   */
  async writeControl(cmd, responseCmd, data = [], critical = true) {
    if (!this.characteristic) throw new Error('Printer not connected');
    const packet = this.buildPacket(cmd, data);

    // Pasang listener SEBELUM write — tidak ada race condition
    const ackPromise = this.prepareAck(responseCmd, 4000);

    for (let i = 0; i < packet.length; i += 20) {
      const chunk = packet.slice(i, i + 20);
      const isLast = (i + 20 >= packet.length);
      if (isLast) {
        try {
          await this.characteristic.writeValueWithResponse(chunk);
        } catch (e) {
          await this.characteristic.writeValueWithoutResponse(chunk);
        }
      } else {
        await this.characteristic.writeValueWithoutResponse(chunk);
        await new Promise(r => setTimeout(r, 5));
      }
    }

    const ack = await ackPromise;
    if (!ack && critical) {
      console.warn(`[NIIMBOT] Cmd 0x${cmd.toString(16)} timed out waiting for 0x${responseCmd.toString(16)}`);
      throw new Error(`Cmd 0x${cmd.toString(16)} timed out waiting for 0x${responseCmd.toString(16)}`);
    }
    return ack;
  }

  async writeData(cmd, data = []) {
    if (!this.characteristic) throw new Error('Printer not connected');
    const packet = this.buildPacket(cmd, data);
    for (let i = 0; i < packet.length; i += 20) {
      await this.characteristic.writeValueWithoutResponse(packet.slice(i, i + 20));
      await new Promise(r => setTimeout(r, 2));
    }
  }

  // ─── Handshake ────────────────────────────────────────────────────────────

  async handshake() {
    console.log('[NIIMBOT] Handshake...');
    // 0xC1 → 0xC2
    const ack = await this.writeControl(0xC1, 0xC2, [0x01], true);
    if (ack && ack.length > 4) {
      this.protocolVersion = ack[4];
      this.isV3 = (this.protocolVersion >= 3);
      console.log(`[NIIMBOT] Protocol V${this.protocolVersion} (isV3=${this.isV3})`);
    }
    if (this.isV3) {
      // 0xDC → 0xD9
      await this.writeControl(0xDC, 0xD9, [0x04], false);
    }
    console.log('[NIIMBOT] Handshake OK');
  }

  // ─── Status ───────────────────────────────────────────────────────────────

  async getStatus() {
    // V3 responds 0xB3, V2 responds 0xA4
    const expectedResp = this.isV3 ? 0xB3 : 0xA4;
    const ack = await this.writeControl(0xA3, expectedResp, [0x01], false);
    if (ack && ack.length > 4) {
      const s = ack[4];
      return {
        lidOpen:    !!(s & 0x01),
        noPaper:    !!(s & 0x02),
        lowBattery: !!(s & 0x04),
        isBusy:     !!(s & 0x10) || !!(s & 0x20)
      };
    }
    return null;
  }

  async waitReady(timeoutMs = 6000) {
    const start = Date.now();
    console.log('[NIIMBOT] Waiting for printer ready...');
    while (Date.now() - start < timeoutMs) {
      const s = await this.getStatus();
      if (s && !s.isBusy) {
        if (s.lidOpen)  throw new Error('Printer Error: Lid Open');
        if (s.noPaper)  throw new Error('Printer Error: No Paper');
        return s;
      }
      await new Promise(r => setTimeout(r, 200));
    }
    throw new Error('Printer busy / not responding');
  }

  // ─── Print Job ────────────────────────────────────────────────────────────

  async print(canvas) {
    if (!this.characteristic) throw new Error('Printer not connected');
    if (this.isPrinting)      throw new Error('Already printing');
    this.isPrinting = true;

    try {
      const width      = Math.min(canvas.width, this.MAX_WIDTH);
      const height     = canvas.height;
      const bytesPerRow = Math.ceil(width / 8);

      const ctx     = canvas.getContext('2d');
      const imgData = ctx.getImageData(0, 0, width, height).data;

      console.time('[NIIMBOT] Print Job');
      console.log(`[NIIMBOT] ${width}x${height}px | bytesPerRow=${bytesPerRow} | V${this.protocolVersion}`);

      await this.waitReady();

      // ── Init ──────────────────────────────────────────────────────────────
      await this.writeControl(0x21, 0x31, [0x05], false);   // SetDensity → 0x31
      await this.writeControl(0x23, 0x33, [0x01], false);   // SetLabelType → 0x33
      await this.writeControl(0x01, 0x02, [                 // PrintStart → 0x02
        0x00, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00
      ], true);

      const st = await this.getStatus();
      if (st && (st.lidOpen || st.noPaper)) {
        throw new Error(st.lidOpen ? 'Lid Open' : 'No Paper');
      }

      // ── Page Setup ────────────────────────────────────────────────────────
      await this.writeControl(0x03, 0x04, [0x01], true);    // PageStart → 0x04

      /**
       * SetPageSize — perbedaan V2 vs V3:
       *
       * V2: CMD 0x13 → ACK 0x14, cols = bytesPerRow
       * V3: CMD 0x1A → ACK 0x1B (hanya jika length=1, bukan DeviceInfo)
       *     cols = pixel width langsung
       *
       * Dari log: ACK 0x1B yang kita terima panjangnya 0x2A (42 byte) = DeviceInfo async.
       * ACK setPageSize yang sah untuk V3 = 0x1B dengan length=1.
       * prepareAck() sudah diupdate untuk skip 0x1B panjang.
       */
      if (this.isV3) {
        console.log(`[NIIMBOT] SetPageSize V3 (0x1A): ${height}h x ${width}w px`);
        await this.writeControl(0x1A, 0x1B, [
          (height >> 8) & 0xFF, height & 0xFF,
          (width  >> 8) & 0xFF, width  & 0xFF,
          0x00, 0x01
        ], true);
      } else {
        console.log(`[NIIMBOT] SetPageSize V2 (0x13): ${height}h x ${bytesPerRow}bpr`);
        await this.writeControl(0x13, 0x14, [
          (height      >> 8) & 0xFF, height      & 0xFF,
          (bytesPerRow >> 8) & 0xFF, bytesPerRow & 0xFF,
          0x00, 0x01
        ], true);
      }
      console.log('[NIIMBOT] SetPageSize OK — sending bitmap...');

      // ── Bitmap Rows ───────────────────────────────────────────────────────
      let totalBlack = 0, rowsWithData = 0;

      for (let y = 0; y < height; y++) {
        const rowBits = new Uint8Array(bytesPerRow);
        let blackCount = 0;

        for (let x = 0; x < width; x++) {
          const idx  = (y * width + x) * 4;
          const gray = (imgData[idx] + imgData[idx+1] + imgData[idx+2]) / 3;
          if (gray < 150) {
            rowBits[Math.floor(x / 8)] |= (1 << (7 - (x % 8)));
            blackCount++;
          }
        }

        totalBlack += blackCount;
        if (blackCount > 0) rowsWithData++;
        if (rowsWithData === 1) console.log(`[NIIMBOT] First black row y=${y} count=${blackCount}`);

        if (blackCount > 0) {
          await this.writeData(0x85, [
            (y >> 8) & 0xFF, y & 0xFF,
            0x00,
            blackCount & 0xFF, (blackCount >> 8) & 0xFF,
            0x01,
            ...rowBits
          ]);
        } else {
          await this.writeData(0x84, [(y >> 8) & 0xFF, y & 0xFF, 0x01]);
        }

        if (y % 5 === 0) await new Promise(r => setTimeout(r, 20));
      }

      console.log(`[NIIMBOT] Bitmap done: ${rowsWithData}/${height} rows, ${totalBlack} black px`);
      if (totalBlack === 0) console.error('[NIIMBOT] WARNING: Canvas kosong!');

      // ── End ───────────────────────────────────────────────────────────────
      await this.writeControl(0xE3, 0xE4, [0x01], true);   // PageEnd → 0xE4

      // Tunggu printer selesai proses sebelum printEnd
      let retry = 10;
      while (retry-- > 0) {
        const s = await this.getStatus();
        if (s && !s.isBusy) break;
        await new Promise(r => setTimeout(r, 300));
      }

      await this.writeControl(0xF3, 0xF4, [0x01], true);   // PrintEnd → 0xF4

      if (this.isV3) {
        // Post-print heartbeat (optional, wait short time)
        await this.writeControl(0xDC, 0xD9, [0x05], false);
      }

      console.timeEnd('[NIIMBOT] Print Job');
      console.log('[NIIMBOT] Done!');

    } catch (err) {
      console.error('[NIIMBOT] Print Error:', err);
      throw err;
    } finally {
      this.isPrinting = false;
    }
  }

  // ─── Test ─────────────────────────────────────────────────────────────────

  async printTestPattern(w = 320, h = 240) {
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0,0,w,h);
    ctx.fillStyle = '#000';
    ['top','bot','left','right'].forEach(s => {
      if (s==='top')   ctx.fillRect(0,0,w,3);
      if (s==='bot')   ctx.fillRect(0,h-3,w,3);
      if (s==='left')  ctx.fillRect(0,0,3,h);
      if (s==='right') ctx.fillRect(w-3,0,3,h);
    });
    ctx.fillRect(0,0,w,28);
    ctx.fillStyle='#fff'; ctx.font='bold 15px Arial'; ctx.textAlign='center';
    ctx.fillText('NIIMBOT B4 TEST',w/2,19);
    ctx.fillStyle='#000'; ctx.font='12px Arial'; ctx.textAlign='left';
    ctx.fillText(`${w}x${h}px | Protocol V${this.protocolVersion}`,8,46);
    ctx.fillText(`SetPageSize: ${this.isV3?'0x1A (V3 pixel)':'0x13 (V2 bytes)'}`,8,62);
    ctx.fillText(new Date().toLocaleTimeString(),8,78);
    ctx.fillRect(8,92,w-16,16);
    ctx.fillStyle='#fff'; ctx.font='10px Arial'; ctx.textAlign='center';
    ctx.fillText('DENSITY',w/2,103);
    ctx.fillStyle='#000';
    for(let i=0;i<20;i++) if(i%2===0) ctx.fillRect(8+i*((w-16)/20),h-40,(w-16)/20,28);
    await this.print(cv);
    console.log('[NIIMBOT] Test pattern done');
  }

  async printSolidBlack(w=200,h=100) {
    const cv = document.createElement('canvas');
    cv.width=w; cv.height=h;
    cv.getContext('2d').fillRect(0,0,w,h);
    await this.print(cv);
  }

  async disconnect() {
    try {
      if (this.characteristic) await this.characteristic.stopNotifications().catch(()=>{});
      if (this.device?.gatt.connected) await this.device.gatt.disconnect();
    } catch(e) {}
    this.device=null; this.server=null; this.characteristic=null;
  }
}

window.NiimbotB4 = new NiimbotB4();
